import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import DebugReloadGuard from './debug/DebugReloadGuard';

// Public Pages
import AproposPage from './pages/public/AproposPage';
import DevisPage from './pages/public/DevisPage';
import ContactPage from './pages/public/ContactPage';
import ElectribornePage from './pages/public/ElectribornePage';
import ServicesPage from './pages/public/ServicesPage';
import BlogPage from './pages/public/BlogPage';
import BlogPostPage from './pages/public/BlogPostPage';
import RequestQuote from './pages/public/RequestQuote';
import Simulator from './pages/public/Simulator/Simulator';
import SimulationMaison from './pages/public/SimulationMaison';
import SimulationCopropriete from './pages/public/SimulationCopropriete';
import SimulationEntreprise from './pages/public/SimulationEntreprise';

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import UserManagement from './pages/admin/UserManagement';
import AddUser from './pages/admin/AddUser';
import EditUser from './pages/admin/EditUser';
import CompanyManagement from './pages/admin/CompanyManagement';
import ViewCompany from './pages/admin/ViewCompany';
import AddCompany from './pages/admin/AddCompany';
import EditCompany from './pages/admin/EditCompany';
import RequestManagement from './pages/admin/RequestManagement';
import AssignRequestPage from './pages/admin/AssignRequestPage'
import ViewRequest from './pages/admin/ViewRequest';
import EditRequest from './pages/admin/EditRequest';
import AddRequest from './pages/admin/AddRequest';
import QuoteGeneration from './pages/admin/QuoteGeneration';
import AddQuote from './pages/admin/AddQuote';
import EditQuote from './pages/admin/EditQuote';
import ViewQuote from './pages/admin/ViewQuote';
import PaymentManagement from './pages/admin/PaymentManagement';
import PaymentDashboard from './pages/admin/PaymentDashboard';
import ViewPayment from './pages/admin/ViewPayment';
import EditPayment from './pages/admin/EditPayment';
import AddPayment from './pages/admin/AddPayment';
import ReportsManagement from './pages/admin/ReportsManagement';
import SiteCustomization from './pages/admin/SiteCustomization';
import MissionManagement from './pages/admin/MissionManagement';
import AddMission from './pages/admin/AddMission';
import ViewMission from './pages/admin/ViewMission';
import EditMission from './pages/admin/EditMission';
import ViewUser from './pages/admin/ViewUser';
import ServiceTypeManagement from './pages/admin/ServiceTypeManagement';
import AddServiceType from './pages/admin/AddServiceType';
import EditServiceType from './pages/admin/EditServiceType';
import AddReport from './pages/admin/AddReport';
import AdViewReport from './pages/admin/ViewReport';
import EditReport from './pages/admin/EditReport';
import EquipmentManagement from './pages/admin/EquipmentManagement';
import AddEquipment from './pages/admin/AddEquipment';
import EditEquipment from './pages/admin/EditEquipment';
import InvoiceManagement from './pages/admin/InvoiceManagement';
import AddInvoice from './pages/admin/AddInvoice';
import ViewInvoice from './pages/admin/ViewInvoice';
import EditInvoice from './pages/admin/EditInvoice';

// Technician Pages
import TechDashboard from './pages/technician/Dashboard';
import TechSchedule from './pages/technician/Schedule';
import TechMissions from './pages/technician/Missions';
import TechReports from './pages/technician/Reports';
import SaveReport from './pages/technician/SaveReport';
import TechEditReport  from './pages/technician/EditReport';
import ViewReport from './pages/technician/ViewReport';
import TechMessaging from './pages/technician/Messaging';
import TechnicalReport from './pages/technician/TechnicalReport';

import TechQuotes from './pages/technician/Quotes';
import CreateQuote from './pages/technician/CreateQuote';
import TechAvailability from './pages/technician/Availability';

import TechnicianReportViewer from './pages/technician/TechnicianReportViewer';
import ServicesGallery from './pages/technician/ServicesGallery';
import AddNewGallery from './pages/technician/AddNewGallery';
import GalleryViewer from './pages/technician/GalleryViewer';

// Client Pages
import ClientDashboard from './pages/client/Dashboard';
import ServiceRequest from './pages/client/ServiceRequest';
import ClientQuotes from './pages/client/Quotes';
import ClientMission  from './pages/client/Missions';
import ClientInvoices from './pages/client/Invoices';
import ClientPayments from './pages/client/Payments';
import ClientMessaging from './pages/client/Messaging';
import StripePayment from './pages/client/StripePayment';
import QuoteDetails from './pages/client/QuoteDetails';

// Shared Pages
import Profile from './pages/shared/Profile';
import Messages from './pages/shared/Messages';

// Redirection selon le rôle
const DashboardRedirect: React.FC = () => {
  const { user } = useAuth();
  if (user?.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  if (user?.role === 'technician') return <Navigate to="/tech/dashboard" replace />;
  if (user?.role === 'client') return <Navigate to="/client/dashboard" replace />;
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <>
    {import.meta.env.DEV && <DebugReloadGuard blockReload={true} />}
    <div className="min-h-screen bg-gray-50">
      <Toaster
        position="top-right"
        toastOptions={{ duration: 4000, style: { background: '#363636', color: '#fff' } }}
      />
      <Routes>
        {/* ===== PUBLIC ===== */}
        <Route path="/" element={<ElectribornePage />} />
        <Route path="/a-propos" element={<AproposPage />} />
        <Route path="/devis" element={<DevisPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:postId" element={<BlogPostPage />} />
        <Route path="/request-quote" element={<RequestQuote />} />
        <Route path="/simulateur/maison" element={<SimulationMaison />} />
        <Route path="/simulateur/copropriete" element={<SimulationCopropriete />} />
        <Route path="/simulateur/entreprise" element={<SimulationEntreprise />} />
        <Route path="/simulateur" element={<Simulator />} />
        <Route path="/simulateur" element={<Simulator />} />

        {/* ===== AUTH ===== */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* ===== ADMIN ===== */}
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute roles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/add"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/view/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <ViewUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/edit/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <EditUser />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/companies"
          element={
            <ProtectedRoute roles={['admin']}>
              <CompanyManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/companies/add"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddCompany />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/companies/view/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <ViewCompany />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/companies/edit/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <EditCompany />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/requests"
          element={
            <ProtectedRoute roles={['admin']}>
              <RequestManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/requests/add"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddRequest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/requests/view/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <ViewRequest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/requests/edit/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <EditRequest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/requests/assign/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <AssignRequestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/quotes"
          element={
            <ProtectedRoute roles={['admin']}>
              <QuoteGeneration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/quotes/add"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddQuote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/quotes/edit/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddQuote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/quotes/view/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <ViewQuote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <ProtectedRoute roles={['admin']}>
              <PaymentManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payments/add"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddPayment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payments/view/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <ViewPayment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payments/edit/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <EditPayment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payment-dashboard"
          element={
            <ProtectedRoute roles={['admin']}>
              <PaymentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute roles={['admin']}>
              <ReportsManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports/add"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports/edit/:reportId"
          element={
            <ProtectedRoute roles={['admin']}>
              <EditReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports/view/:reportId"
          element={
            <ProtectedRoute roles={['admin']}>
              <AdViewReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/personnalisation"
          element={
            <ProtectedRoute roles={['admin']}>
              <SiteCustomization />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/service-types"
          element={
            <ProtectedRoute roles={['admin']}>
              <ServiceTypeManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/service-types/add"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddServiceType />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/service-types/edit/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <EditServiceType />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/missions"
          element={
            <ProtectedRoute roles={['admin']}>
              <MissionManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/add-mission"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddMission />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/missions/view/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <ViewMission />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/missions/edit/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <EditMission />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/equipments/add"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddEquipment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/equipments"
          element={
            <ProtectedRoute roles={['admin']}>
              <EquipmentManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/equipments/edit/:type/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <EditEquipment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/invoices/add"
          element={
            <ProtectedRoute roles={['admin']}>
              <AddInvoice />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/invoices"
          element={
            <ProtectedRoute roles={['admin']}>
              <InvoiceManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/invoices/view/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <ViewInvoice />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/invoices/edit/:id"
          element={
            <ProtectedRoute roles={['admin']}>
              <EditInvoice />
            </ProtectedRoute>
          }
        />

        {/* ===== TECH ===== */}
        <Route
          path="/tech/dashboard"
          element={
            <ProtectedRoute roles={['technician']}>
              <TechDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/schedule"
          element={
            <ProtectedRoute roles={['technician']}>
              <TechSchedule />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/missions"
          element={
            <ProtectedRoute roles={['technician']}>
              <TechMissions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/missions/:id"
          element={
            <ProtectedRoute roles={['technician']}>
              <ViewMission />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/reports"
          element={
            <ProtectedRoute roles={['technician']}>
              <TechReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/reports/add"
          element={
            <ProtectedRoute roles={['technician']}>
              <SaveReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/reports/edit/:reportId"
          element={
            <ProtectedRoute roles={['technician']}>
              <TechEditReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/reports/view/:id"
          element={
            <ProtectedRoute roles={['technician']}>
              <ViewReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/messages"
          element={
            <ProtectedRoute roles={['technician']}>
              <TechMessaging />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/quotes"
          element={
            <ProtectedRoute roles={['technician']}>
              <TechQuotes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/quotes/create"
          element={
            <ProtectedRoute roles={['technician']}>
              <CreateQuote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/quotes/create/:interventionId"
          element={
            <ProtectedRoute roles={['technician']}>
              <CreateQuote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/availability"
          element={
            <ProtectedRoute roles={['technician']}>
              <TechAvailability />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/services-gallery"
          element={
            <ProtectedRoute roles={['technician']}>
              <ServicesGallery />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/add-new-gallery"
          element={
            <ProtectedRoute roles={['technician']}>
              <AddNewGallery />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tech/gallery-viewer/:serviceId"
          element={
            <ProtectedRoute roles={['technician']}>
              <GalleryViewer />
            </ProtectedRoute>
          }
        />

        {/* ===== CLIENT ===== */}
        <Route
          path="/client/dashboard"
          element={
            <ProtectedRoute roles={['client']}>
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/request"
          element={
            <ProtectedRoute roles={['client']}>
              <ServiceRequest />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/quotes"
          element={
            <ProtectedRoute roles={['client']}>
              <ClientQuotes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/quotes/:quoteId"
          element={
            <ProtectedRoute roles={['client']}>
              <QuoteDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/missions"
          element={
            <ProtectedRoute roles={['client']}>
              <ClientMission  />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/Invoices"
          element={
            <ProtectedRoute roles={['client']}>
              <ClientInvoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/payments"
          element={
            <ProtectedRoute roles={['client']}>
              <ClientPayments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/payment/:quoteId"
          element={
            <ProtectedRoute roles={['client']}>
              <StripePayment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/messages"
          element={
            <ProtectedRoute roles={['client']}>
              <ClientMessaging />
            </ProtectedRoute>
          }
        />

        {/* ===== Shared ===== */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          }
        />

        {/* ===== Dashboard redirect by role ===== */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRedirect />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
    </>
  );
}

export default App;